import { COLOR } from "../config.ts";
import { clamp, damp } from "../core/vec.ts";
import type { SpawnContext } from "../content/levelTypes.ts";

/**
 * Enemies are creatures that fly formation paths through the arena and attack.
 * With the sword online, most can be slain — each has hit points and dies to
 * slashes — but every one also leaves on its own schedule, so ignoring them is
 * a valid (harder) route.
 *
 * The seven archetypes:
 *   METEOR  — a burning rock flying in a straight line; slow and vertical in
 *             early levels, fast and aimed in later ones. 1 hit.
 *   ARCHER  — an octopod that settles on a post and fires 5 volleys of 3
 *             darts, then wanders off, loosing a couple of parting arrows
 *             on the way out. 2 hits.
 *   BOMBER  — waddles in and hurls a FIVE-bomb volley three times over (each
 *             bomb detonates into a ring), then turns around and walks away.
 *             Tanky: 4 hits.
 *   HEN     — the Healing Hen: hops 8 arcs aimed at the player, then bounds
 *             off stage. 3 hits to KO.
 *   BEAST   — a huge charger: telegraphs, then thunders across the arena in a
 *             horizontal or vertical line through the player. 3 hits.
 *   LASER   — a floating eyeball of energy that charges up, then fires a
 *             sustained laser along its locked aim. 3 hits.
 *   NINJA   — crouches to lock on, then flip-dashes through the player's
 *             position; lands, re-aims, and repeats — seven dashes total, then
 *             it exits the screen. Fragile: 1 hit.
 *
 * Enemies are pooled; `reset()` fully reinitializes a recycled instance.
 */
export type EnemyType = 0 | 1 | 2 | 3 | 4 | 5 | 6;
export const EN_METEOR: EnemyType = 0;
export const EN_ARCHER: EnemyType = 1;
export const EN_BOMBER: EnemyType = 2;
export const EN_HEN: EnemyType = 3;
export const EN_BEAST: EnemyType = 4;
export const EN_LASER: EnemyType = 5;
export const EN_NINJA: EnemyType = 6;

/** Behaviour state shared by all types. */
export const MODE_ENTER = 0;
export const MODE_ACT = 1;
export const MODE_LEAVE = 2;

export const ENEMY_COLORS: readonly number[] = [
  COLOR.enemyMeteor,
  COLOR.enemyArcher,
  COLOR.enemyBomber,
  COLOR.enemyHen,
  COLOR.enemyBeast,
  COLOR.enemyLaser,
  COLOR.enemyNinja,
];

const ENEMY_RADII: readonly number[] = [20, 24, 22, 20, 46, 22, 20];
const ENEMY_HP: readonly number[] = [1, 2, 4, 3, 3, 3, 1];
// Archer/hen exits are act-count driven (volleys/hops); life is a generous fallback.
const ENEMY_LIFE: readonly number[] = [999, 30, 26, 30, 24, 19, 20];

const HEN_GRAVITY = 1300;
const HEN_HOP_TIME = 0.8;
const HEN_HOPS = 8; // attacking hops before she bounds off stage
const ARCHER_VOLLEYS = 5; // 3-dart volleys before wandering off
const ARCHER_PARTING_ARROWS = 2; // single shots fired while leaving
const BOMBER_BOMBS = 5; // bombs hurled per throw
const BOMBER_VOLLEYS = 3; // number of five-bomb throws before walking off
const BEAST_CHARGE_SPEED = 950;
const NINJA_DASH_SPEED = 1150;
const NINJA_OVERSHOOT = 90; // dash carries this far past the player's mark
const NINJA_DASHES = 7;
const NINJA_CROUCH = 0.7;

export class Enemy {
  type: EnemyType = EN_METEOR;

  x = 0;
  y = 0;
  px = 0;
  py = 0;
  vx = 0;
  vy = 0;

  radius = 20;
  color = COLOR.enemyMeteor;
  angle = 0; // facing (render + some AI)

  age = 0;
  life = 10; // seconds until the enemy departs on its own
  fade = 0; // spawn fade-in [0..1]
  dying = 0; // death-animation countdown; > 0 means no collision
  grazed = false;

  hp = 1;
  maxHp = 1;
  hitFlash = 0; // white flash timer after taking a sword hit

  mode = MODE_ENTER;
  t = 0; // generic state timer
  fireT = 0; // cooldown until next attack
  phase = 0; // per-type sub-state (volley darts left, hop state, axis…)
  count = 0; // per-type act counter (hen hops, archer volleys, parting shots)
  aimX = 0; // locked aim direction / charge direction
  aimY = 0;
  homeX = 0; // post / stop point / hop ground line
  homeY = 0;
  speed = 100;
  /** While true the enemy is exempt from offscreen culling (Beast between charges). */
  noCull = false;
  /** EN_LASER only: exit sideways toward the nearest edge instead of drifting
   * straight up. Opt-in via spawn opts. */
  laserExitSide = false;

  reset(type: EnemyType, x: number, y: number, opts?: Partial<Enemy>): void {
    this.type = type;
    this.x = this.px = x;
    this.y = this.py = y;
    this.vx = 0;
    this.vy = 0;
    this.radius = ENEMY_RADII[type];
    this.color = ENEMY_COLORS[type];
    this.angle = 0;
    this.age = 0;
    this.life = ENEMY_LIFE[type];
    this.fade = 0;
    this.dying = 0;
    this.grazed = false;
    this.hp = this.maxHp = ENEMY_HP[type];
    this.hitFlash = 0;
    this.mode = MODE_ENTER;
    this.t = 0;
    this.fireT = 1.2;
    this.phase = 0;
    this.count = 0;
    this.aimX = 0;
    this.aimY = 0;
    this.homeX = x;
    this.homeY = y;
    this.speed = 100;
    this.noCull = type === EN_BEAST;
    this.laserExitSide = false;
    if (type === EN_NINJA) this.t = 0.9; // opening crouch before dash #1
    if (opts) Object.assign(this, opts);
    if (type === EN_HEN) this.phase = 0; // grounded
  }

  /** Advance AI: move and attack. Called only while alive (dying === 0). */
  update(dt: number, ctx: SpawnContext): void {
    if (this.hitFlash > 0) this.hitFlash -= dt;
    switch (this.type) {
      case EN_METEOR:
        this.updateMeteor(dt);
        break;
      case EN_ARCHER:
        this.updateArcher(dt, ctx);
        break;
      case EN_BOMBER:
        this.updateBomber(dt, ctx);
        break;
      case EN_HEN:
        this.updateHen(dt, ctx);
        break;
      case EN_BEAST:
        this.updateBeast(dt, ctx);
        break;
      case EN_LASER:
        this.updateLaser(dt, ctx);
        break;
      case EN_NINJA:
        this.updateNinja(dt, ctx);
        break;
    }
  }

  /** Switch to the departure behaviour (called once when `life` runs out). */
  beginLeave(ctx: SpawnContext): void {
    if (this.mode === MODE_LEAVE) return;
    this.mode = MODE_LEAVE;
    switch (this.type) {
      case EN_ARCHER:
        // Wander off slowly enough to get the parting shots away.
        this.vx = this.x < ctx.arenaW / 2 ? -130 : 130;
        this.vy = 0;
        this.phase = ARCHER_PARTING_ARROWS;
        this.fireT = 0.7;
        break;
      case EN_BOMBER:
        this.vx = this.x < ctx.arenaW / 2 ? -95 : 95;
        this.vy = 0;
        break;
      case EN_BEAST:
        this.noCull = false; // despawn when the current charge exits
        break;
      case EN_LASER:
        if (this.laserExitSide) {
          // Slide off toward the nearest side edge instead of drifting up.
          this.vx = this.x < ctx.arenaW / 2 ? -220 : 220;
          this.vy = 0;
        } else {
          this.vx = 0;
          this.vy = -160;
        }
        break;
      case EN_NINJA: {
        // Exit move: one last flipping dash toward the nearest corner.
        const tx = this.x < ctx.arenaW / 2 ? -220 : ctx.arenaW + 220;
        const ty = this.y < ctx.arenaH / 2 ? -220 : ctx.arenaH + 220;
        const dx = tx - this.x;
        const dy = ty - this.y;
        const d = Math.hypot(dx, dy) || 1;
        this.aimX = dx / d;
        this.aimY = dy / d;
        this.angle = Math.atan2(dy, dx);
        this.homeX = this.t = d / NINJA_DASH_SPEED; // flip over the whole exit
        break;
      }
      default:
        break;
    }
  }

  // --- Meteor ---------------------------------------------------------------
  private updateMeteor(dt: number): void {
    this.x += this.vx * dt;
    this.y += this.vy * dt;
    this.angle = Math.atan2(this.vy, this.vx);
    this.phase += dt * 2.4; // rock tumble (render)
  }

  // --- Archer ---------------------------------------------------------------
  private updateArcher(dt: number, ctx: SpawnContext): void {
    if (this.mode === MODE_LEAVE) {
      // Wander off, loosing a couple of parting arrows aimed at the player.
      this.x += this.vx * dt;
      this.fireT -= dt;
      if (this.phase > 0 && this.fireT <= 0) {
        this.phase--;
        this.fireT = 1.1;
        const dx = ctx.playerX - this.x;
        const dy = ctx.playerY - this.y;
        const d = Math.hypot(dx, dy) || 1;
        this.angle = Math.atan2(dy, dx);
        ctx.bullet(this.x, this.y, (dx / d) * 340, (dy / d) * 340, 7, {
          color: this.color,
        });
        ctx.attack("arrow");
      }
      return;
    }
    if (this.mode === MODE_ENTER) {
      this.x = damp(this.x, this.homeX, 2.6, dt);
      this.y = damp(this.y, this.homeY, 2.6, dt);
      if (this.age > 1.3) {
        this.mode = MODE_ACT;
        this.fireT = 0.6;
      }
      return;
    }
    // Gentle hover on post.
    this.y = this.homeY + Math.sin(this.age * 2) * 8;
    this.fireT -= dt;
    if (this.phase > 0) {
      // Mid-volley: release the remaining darts on the standardized interval.
      if (this.fireT <= 0) {
        this.phase--;
        this.fireT = this.phase > 0 ? 0.14 : 1.9; // dart gap / volley gap
        ctx.bullet(this.x, this.y, this.aimX * 340, this.aimY * 340, 7, {
          color: this.color,
        });
        ctx.attack("arrow");
        // Volley complete: after the 5th, pack up and wander off.
        if (this.phase === 0) {
          this.count++;
          if (this.count >= ARCHER_VOLLEYS) this.beginLeave(ctx);
        }
      }
    } else if (this.fireT <= 0) {
      // Start a volley: lock aim on the player, fire dart 1 of 3.
      const dx = ctx.playerX - this.x;
      const dy = ctx.playerY - this.y;
      const d = Math.hypot(dx, dy) || 1;
      this.aimX = dx / d;
      this.aimY = dy / d;
      this.angle = Math.atan2(dy, dx);
      this.phase = 2; // two more darts to go
      this.fireT = 0.14;
      ctx.bullet(this.x, this.y, this.aimX * 340, this.aimY * 340, 7, {
        color: this.color,
      });
      ctx.attack("arrow");
    }
  }

  // --- Bomber ---------------------------------------------------------------
  private updateBomber(dt: number, ctx: SpawnContext): void {
    if (this.mode === MODE_ENTER) {
      // Waddle in toward the stop point.
      const dir = this.homeX > this.x ? 1 : -1;
      this.x += dir * 95 * dt;
      this.angle = dir > 0 ? 0 : Math.PI;
      if (Math.abs(this.x - this.homeX) < 8) {
        this.mode = MODE_ACT;
        this.t = 0.6; // wind-up before the throw
      }
      return;
    }
    if (this.mode === MODE_ACT) {
      this.t -= dt;
      this.angle = Math.atan2(ctx.playerY - this.y, ctx.playerX - this.x);
      if (this.t <= 0) {
        // Hurl a five-bomb volley — one dead on the player, four bracketing the
        // landing zone. All share the same flight time, so they detonate
        // together in a rolling barrage. He throws three such volleys before
        // he's out of satchel.
        const T = 1.05;
        const g = 700;
        for (let i = 0; i < BOMBER_BOMBS; i++) {
          const spreadA = (i / BOMBER_BOMBS) * Math.PI * 2;
          const spreadR = i === 0 ? 0 : 130 + ctx.rng.range(-25, 25);
          const tx = ctx.playerX + Math.cos(spreadA) * spreadR;
          const ty = ctx.playerY + Math.sin(spreadA) * spreadR * 0.7;
          const bvx = (tx - this.x) / T;
          const bvy = (ty - this.y) / T - 0.5 * g * T;
          ctx.bullet(this.x, this.y, bvx, bvy, 13, {
            color: COLOR.accent,
            ay: g,
            life: T,
            burstOnExpire: 10,
            burstSpeed: 270,
            cullOffscreen: false,
          });
        }
        ctx.attack("bomb");
        this.count++;
        if (this.count >= BOMBER_VOLLEYS) {
          this.beginLeave(ctx); // satchel spent: turn around and walk away
        } else {
          this.t = 1.7; // reload / wind up the next volley
        }
      }
      return;
    }
    // Leaving: waddle out.
    this.x += this.vx * dt;
    this.angle = this.vx > 0 ? 0 : Math.PI;
  }

  // --- Healing Hen ------------------------------------------------------------
  private updateHen(dt: number, ctx: SpawnContext): void {
    if (this.phase === 0) {
      // Grounded squat between hops.
      this.t -= dt;
      if (this.t <= 0) {
        // Her set is 7 attacking hops; after that she bounds off stage.
        if (this.mode !== MODE_LEAVE && this.count >= HEN_HOPS) this.beginLeave(ctx);
        // Launch an arc TOWARD the player (or the nearest edge when leaving).
        // Undershoot and cap the hop so she pressures without homing onto the
        // player's exact pixel — the threat is her body arcing at you.
        let tx = ctx.playerX;
        let ty = ctx.playerY;
        if (this.mode === MODE_LEAVE) {
          tx = this.x < ctx.arenaW / 2 ? -160 : ctx.arenaW + 160;
          ty = this.y;
        } else {
          let dx = (tx - this.x) * 0.72;
          let dy = (ty - this.y) * 0.72;
          const d = Math.hypot(dx, dy);
          const maxHop = 340;
          if (d > maxHop) {
            dx = (dx / d) * maxHop;
            dy = (dy / d) * maxHop;
          }
          tx = this.x + dx;
          ty = this.y + dy;
        }
        this.vx = (tx - this.x) / HEN_HOP_TIME;
        this.vy = (ty - this.y) / HEN_HOP_TIME - 0.5 * HEN_GRAVITY * HEN_HOP_TIME;
        this.phase = 1;
        this.t = HEN_HOP_TIME;
        ctx.attack("hop");
        if (this.mode !== MODE_LEAVE) this.count++; // attacking hops only
      }
      return;
    }
    // Airborne: ballistic arc.
    this.vy += HEN_GRAVITY * dt;
    this.x += this.vx * dt;
    this.y += this.vy * dt;
    this.angle = this.vx >= 0 ? 0 : Math.PI;
    this.t -= dt;
    if (this.t <= 0) {
      this.phase = 0;
      this.t = 0.45; // squat
      this.vx = 0;
      this.vy = 0;
    }
  }

  // --- Charging Beast -----------------------------------------------------------
  private updateBeast(dt: number, ctx: SpawnContext): void {
    // phase: 0 = horizontal lanes, 1 = vertical lanes (set at spawn).
    if (this.mode === MODE_ENTER) {
      // Telegraph at the edge: track the player's lane for the first half,
      // then lock so the charge is dodgeable.
      this.t += dt;
      const track = this.t < 0.55;
      if (this.phase === 0) {
        if (track) this.y = damp(this.y, ctx.playerY, 6, dt);
        this.aimX = this.x < ctx.arenaW / 2 ? 1 : -1;
        this.aimY = 0;
        this.angle = this.aimX > 0 ? 0 : Math.PI;
      } else {
        if (track) this.x = damp(this.x, ctx.playerX, 6, dt);
        this.aimX = 0;
        this.aimY = this.y < ctx.arenaH / 2 ? 1 : -1;
        this.angle = this.aimY > 0 ? Math.PI / 2 : -Math.PI / 2;
      }
      if (this.t >= 1.05) {
        this.mode = MODE_ACT; // CHARGE
        ctx.attack("charge");
      }
      return;
    }
    if (this.mode === MODE_ACT) {
      this.x += this.aimX * BEAST_CHARGE_SPEED * dt;
      this.y += this.aimY * BEAST_CHARGE_SPEED * dt;
      const out = 90;
      const exited =
        this.x < -out || this.x > ctx.arenaW + out || this.y < -out || this.y > ctx.arenaH + out;
      if (exited) {
        if (this.life <= 2.2 || !this.noCull) {
          this.beginLeave(ctx); // no time for another pass: stay gone
          return;
        }
        // Set up the next charge from the side it exited on. Park partially
        // INSIDE the arena so the telegraph (rumbling body) is visible.
        this.mode = MODE_ENTER;
        this.t = 0;
        const inset = this.radius * 0.35;
        if (this.phase === 0) {
          this.x = this.x < 0 ? -inset : ctx.arenaW + inset;
          this.y = ctx.playerY;
        } else {
          this.y = this.y < 0 ? -inset : ctx.arenaH + inset;
          this.x = ctx.playerX;
        }
      }
      return;
    }
    // MODE_LEAVE while offscreen: keep moving out until culled.
    this.x += this.aimX * BEAST_CHARGE_SPEED * dt;
    this.y += this.aimY * BEAST_CHARGE_SPEED * dt;
  }

  // --- Laser Eye -----------------------------------------------------------------
  private updateLaser(dt: number, ctx: SpawnContext): void {
    if (this.mode === MODE_LEAVE) {
      this.x += this.vx * dt;
      this.y += this.vy * dt;
      return;
    }
    if (this.mode === MODE_ENTER) {
      this.x = damp(this.x, this.homeX, 2.4, dt);
      this.y = damp(this.y, this.homeY, 2.4, dt);
      if (this.age > 1.4) {
        this.mode = MODE_ACT;
        this.phase = 0; // idle
        this.t = 1.0;
      }
      return;
    }
    // Hover bob.
    this.y = this.homeY + Math.sin(this.age * 1.8) * 10;
    this.t -= dt;
    if (this.phase === 0) {
      // Idle: iris tracks the player.
      this.angle = Math.atan2(ctx.playerY - this.y, ctx.playerX - this.x);
      if (this.t <= 0) {
        // Lock aim and start charging (the visible windup is the telegraph).
        this.phase = 1;
        this.t = 1.25;
        this.aimX = Math.cos(this.angle);
        this.aimY = Math.sin(this.angle);
      }
    } else if (this.phase === 1) {
      if (this.t <= 0) {
        // FIRE: a long beam along the locked aim, brief flash-warn for readability.
        const a = this.angle;
        const cx = this.x + this.aimX * 880;
        const cy = this.y + this.aimY * 880;
        ctx.rect(cx, cy, 880, 13, a, {
          warnT: 0.12,
          warnTotal: 0.12,
          life: 0.5,
          cullOffscreen: false,
          color: COLOR.enemyLaser,
        });
        ctx.attack("laser");
        this.phase = 2;
        this.t = 0.6; // recoil / beam duration
      }
    } else if (this.t <= 0) {
      this.phase = 0;
      this.t = 1.6; // cooldown before tracking again
    }
  }

  // --- Ninja -----------------------------------------------------------------------
  // Modes double as the attack cycle: ENTER = crouched & aiming (the
  // telegraph), ACT = airborne flipping dash. `phase` counts dashes;
  // `homeX` stores the current dash's total duration for the render flip.
  private updateNinja(dt: number, ctx: SpawnContext): void {
    if (this.mode === MODE_LEAVE) {
      // Final dash off the screen, still flipping.
      this.t -= dt;
      this.x += this.aimX * NINJA_DASH_SPEED * dt;
      this.y += this.aimY * NINJA_DASH_SPEED * dt;
      return;
    }
    if (this.mode === MODE_ENTER) {
      // Crouch: track the player while winding up, then commit.
      this.t -= dt;
      this.angle = Math.atan2(ctx.playerY - this.y, ctx.playerX - this.x);
      if (this.t > 0) return;
      if (this.phase >= NINJA_DASHES) {
        this.beginLeave(ctx); // seven dashes done → vanish offscreen
        return;
      }
      // Lock a dash through the player's current position (with overshoot),
      // clamped so the landing spot stays on-screen for the next crouch.
      const dx = ctx.playerX - this.x;
      const dy = ctx.playerY - this.y;
      const d = Math.hypot(dx, dy) || 1;
      const tx = clamp(this.x + (dx / d) * (d + NINJA_OVERSHOOT), 50, ctx.arenaW - 50);
      const ty = clamp(this.y + (dy / d) * (d + NINJA_OVERSHOOT), 50, ctx.arenaH - 50);
      const ddx = tx - this.x;
      const ddy = ty - this.y;
      const dd = Math.hypot(ddx, ddy) || 1;
      this.aimX = ddx / dd;
      this.aimY = ddy / dd;
      this.angle = Math.atan2(ddy, ddx);
      this.homeX = this.t = dd / NINJA_DASH_SPEED;
      this.mode = MODE_ACT;
      this.phase++;
      ctx.attack("dash");
      return;
    }
    // MODE_ACT: airborne flipping dash toward the marked landing spot.
    this.t -= dt;
    this.x += this.aimX * NINJA_DASH_SPEED * dt;
    this.y += this.aimY * NINJA_DASH_SPEED * dt;
    if (this.t <= 0) {
      this.mode = MODE_ENTER; // land → crouch → re-aim
      this.t = NINJA_CROUCH;
    }
  }
}
